import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  formatDaysAndHours,
  formatHm,
  type OvertimeBalance,
  type OvertimeInputMode,
  type OvertimeRequestSummary,
} from './types';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function OvertimeRequestModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();

  const balance = useQuery({
    queryKey: ['overtime', 'balance'],
    queryFn: () => api<OvertimeBalance>('/overtime/balance'),
    enabled: open,
  });

  const [mode, setMode] = useState<OvertimeInputMode>('DAYS');
  const [amount, setAmount] = useState<string>('');
  const [comment, setComment] = useState('');

  const parsed = useMemo(() => Number.parseFloat(amount.replace(',', '.')), [amount]);
  const dayMinutes = balance.data?.dayMinutes ?? 480;
  const requestedMinutes = useMemo(() => {
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return mode === 'DAYS'
      ? Math.round(parsed * dayMinutes)
      : Math.round(parsed * 60);
  }, [parsed, mode, dayMinutes]);

  const available = balance.data?.availableMinutes ?? 0;
  const exceeds = requestedMinutes > available;

  const createMutation = useMutation({
    mutationFn: () =>
      api<OvertimeRequestSummary>('/overtime', {
        method: 'POST',
        body: JSON.stringify({
          inputMode: mode,
          amount: parsed,
          comment: comment.trim() || undefined,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime'] });
      onClose();
      reset();
    },
  });

  if (!open) return null;

  function reset() {
    setMode('DAYS');
    setAmount('');
    setComment('');
    createMutation.reset();
  }

  function handleClose() {
    if (createMutation.isPending) return;
    reset();
    onClose();
  }

  const canSubmit =
    requestedMinutes > 0 && !exceeds && !createMutation.isPending && available > 0;

  const error = createMutation.error
    ? createMutation.error instanceof ApiError
      ? createMutation.error.message
      : 'Error al enviar la solicitud'
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        aria-hidden
        onClick={handleClose}
        className="absolute inset-0"
        style={{ background: 'rgba(10,14,31,0.4)', backdropFilter: 'blur(2px)' }}
      />

      <div className="relative w-full max-w-[520px] bg-card rounded-sm shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-[15px] font-semibold tracking-tightish">
            Convertir horas a compensar en días de descanso compensatorio
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar"
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-line-strong bg-card text-foreground hover:border-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) createMutation.mutate();
          }}
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label>Pedir en…</Label>
              <div className="flex items-center gap-5 text-sm">
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="overtime-mode"
                    checked={mode === 'DAYS'}
                    onChange={() => setMode('DAYS')}
                    className="h-3.5 w-3.5 accent-icaria-600"
                  />
                  Días
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="overtime-mode"
                    checked={mode === 'HOURS'}
                    onChange={() => setMode('HOURS')}
                    className="h-3.5 w-3.5 accent-icaria-600"
                  />
                  Horas
                </label>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ot-amount">
                Cantidad <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ot-amount"
                type="number"
                min={mode === 'DAYS' ? 1 : 0.25}
                step={mode === 'DAYS' ? 1 : 0.25}
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-32"
              />
              <span className="text-[11px] text-muted-foreground">
                {mode === 'DAYS'
                  ? `1 día = ${formatHm(dayMinutes)} actualmente`
                  : 'Admite fracciones (p. ej. 1,5 = 1h 30m)'}
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ot-comment">Motivo (opcional)</Label>
              <Textarea
                id="ot-comment"
                rows={3}
                maxLength={500}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>

            <SaldoInfo
              available={available}
              dayMinutes={dayMinutes}
              requested={requestedMinutes}
              exceeds={exceeds}
            />

            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>

          <footer className="flex justify-end gap-2 px-6 py-3.5 border-t border-border bg-paper">
            <Button type="button" variant="ghost" size="sm" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={!canSubmit}>
              {createMutation.isPending ? 'Enviando…' : 'Enviar solicitud'}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function SaldoInfo({
  available,
  dayMinutes,
  requested,
  exceeds,
}: {
  available: number;
  dayMinutes: number;
  requested: number;
  exceeds: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3 px-4 py-2.5 rounded-sm border',
        exceeds
          ? 'bg-destructive/5 border-destructive/40'
          : 'bg-icaria-100 border-icaria-200',
      )}
    >
      <span
        className={cn(
          'text-[13px] font-medium',
          exceeds ? 'text-destructive' : 'text-icaria-700',
        )}
      >
        Saldo a compensar disponible
      </span>
      <span
        className={cn(
          'font-mono text-[13px] font-semibold tabular-nums',
          exceeds ? 'text-destructive' : 'text-icaria-700',
        )}
      >
        {formatDaysAndHours(available, dayMinutes)}
        {requested > 0
          ? ` · usarías ${formatDaysAndHours(requested, dayMinutes)}`
          : ''}
      </span>
    </div>
  );
}
